/**
 * Trail lines for drones (all) and selected target (only).
 * Mockup behaviour: drone breadcrumbs always shown (faint),
 * target breadcrumbs only for `selectedTargetId`.
 */
import type { GeoJSONSource, Map } from 'mapbox-gl';
import { droneFilterKey, visibleFleet } from '@/lib/map/droneFilter';
import { useDronesStore } from '@/lib/store/drones';
import { useTargetsStore } from '@/lib/store/targets';
import { useUiStore } from '@/lib/store/ui';

const SRC = 'src-trails';
const LAYER = 'lyr-trails';
const DOT_LAYER = 'lyr-trails-dots';

function buildFeatures() {
  const features: GeoJSON.Feature[] = [];
  const dotFeatures: GeoJSON.Feature[] = [];
  const drones = visibleFleet();
  for (const d of drones) {
    if (d.trail.length < 2) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: d.trail.slice() },
      properties: { kind: 'drone' },
    });
  }
  const ts = useTargetsStore.getState();
  const selId = ts.selectedId;
  if (selId) {
    const trail = ts.trails[selId];
    if (trail && trail.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: trail.slice() },
        properties: { kind: 'target' },
      });
      // Fading dots along the trail
      trail.forEach((pt, i) => {
        dotFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: pt },
          properties: { kind: 'target-dot', opacity: 0.2 + (0.8 * i) / (trail.length - 1) },
        });
      });
    }
  }
  return { features, dotFeatures };
}

export function ensureTrailsLayer(map: Map) {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addSource(`${SRC}-dots`, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(LAYER)) {
    map.addLayer({
      id: LAYER,
      type: 'line',
      source: SRC,
      paint: {
        'line-color': ['match', ['get', 'kind'], 'target', '#1a7a40', /* drone */ '#22a050'],
        'line-width': ['match', ['get', 'kind'], 'target', 2.5, 1.5],
        'line-opacity': ['match', ['get', 'kind'], 'target', 0.75, 0.45],
        'line-dasharray': [1, 0],
      },
    });
  }
  if (!map.getLayer(DOT_LAYER)) {
    map.addLayer({
      id: DOT_LAYER,
      type: 'circle',
      source: `${SRC}-dots`,
      paint: {
        'circle-radius': 2,
        'circle-color': '#1a7a40',
        'circle-opacity': ['get', 'opacity'],
      },
    });
  }
}

export function syncTrailsLayer(map: Map) {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  const dotSrc = map.getSource(`${SRC}-dots`) as GeoJSONSource | undefined;
  if (!src || !dotSrc) return;
  const { features, dotFeatures } = buildFeatures();
  src.setData({ type: 'FeatureCollection', features });
  dotSrc.setData({ type: 'FeatureCollection', features: dotFeatures });
}

export function subscribeTrailsLayer(map: Map): () => void {
  ensureTrailsLayer(map);
  syncTrailsLayer(map);
  let rafId: number | null = null;
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => { rafId = null; syncTrailsLayer(map); });
  };
  const unsubD = useDronesStore.subscribe((s) => s.byId, schedule);
  const unsubCat = useUiStore.subscribe(droneFilterKey, schedule);
  const unsubT = useTargetsStore.subscribe((s) => s.trails, schedule);
  const unsubSel = useTargetsStore.subscribe((s) => s.selectedId, schedule);
  return () => {
    unsubD();
    unsubCat();
    unsubT();
    unsubSel();
  };
}
